/**
 * WordMill — the working indicator's slot-machine word morph.
 *
 * The status line's "Working" label holds steady for a few seconds, then
 * decomposes and re-forms into another working word (some earnest, some
 * fun): each character is a slot that starts flipping through random
 * letters/symbols — lazily at first, then every frame — before landing on
 * the target word's character and sticking. Longer targets grow new slots
 * mid-morph; shorter ones let trailing slots flip out and vanish. The new
 * word then holds, and the cycle repeats for as long as the turn runs.
 *
 * Pure logic, driven entirely by the caller's clock (`text(now)` once per
 * frame): no timers of its own, deterministic per (slot, time-bucket) while
 * flipping so a re-render within the same bucket shows the same glyph.
 */

export const MILL_WORDS = [
  "Working",
  "Thinking",
  "Building",
  "Brewing",
  "Crafting",
  "Scheming",
  "Tinkering",
  "Noodling",
  "Pondering",
  "Wrangling",
  "Conjuring",
  "Percolating",
  "Assembling",
  "Cogitating",
  "Hatching",
  "Forging",
  // The silly shelf — irreverent, never profane.
  "Pickling",
  "Spooning",
  "Marinating",
  "Squishing",
  "Wiggling",
  "Frolicking",
  "Moisturizing",
  "Bamboozling",
  "Skedaddling",
  "Discombobulating",
  "Waffling",
  "Yodeling",
  "Shimmying",
  "Galumphing",
  "Snorkeling",
  "Bedazzling",
] as const;

// What the slots flip through — letters plus the punctuation "debris" that
// sells the decomposing look.
const FLIP_POOL = "abcdefghjkmnopqrstuvwxyz#$%&@!*+=.:~";

const HOLD_MS = 2600; // how long a landed word stays intact
// Per-slot flip cadence: a slot flips lazily right after it starts
// (LAZY_PERIOD per glyph), then per-frame once it has been flipping a while.
const LAZY_MS = 320;
const LAZY_PERIOD = 200;
const FAST_PERIOD = 70;

const BOLD = "\x1b[1m";
const DIM = "\x1b[2m";
const OFF = "\x1b[22m"; // clears bold + dim

/** Deterministic small hash → pool glyph, stable within one flip bucket. */
function glyphAt(slot: number, bucket: number): string {
  let h = (slot + 1) * 2654435761 ^ (bucket + 1) * 40503;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return FLIP_POOL[(h >>> 0) % FLIP_POOL.length];
}

interface Slot {
  start: number; // ms into the morph when this slot begins flipping
  land: number; // ms into the morph when it locks onto the target char
}

export class WordMill {
  private word: string = MILL_WORDS[0];
  private target: string | null = null; // non-null while morphing
  private phaseAt = 0; // clock time the current phase began
  private slots: Slot[] = [];
  private morphLen = 0;
  private rng: () => number;

  constructor(rng: () => number = Math.random) {
    this.rng = rng;
  }

  /** Restart at "Working" (called when a turn begins). */
  reset(now: number): void {
    this.word = MILL_WORDS[0];
    this.target = null;
    this.phaseAt = now;
  }

  /** The plain word currently displayed or being formed (for tests). */
  get current(): string {
    return this.target ?? this.word;
  }

  /** The styled label for this frame (contains only bold/dim ANSI). */
  text(now: number): string {
    if (this.phaseAt === 0) this.phaseAt = now;
    if (!this.target) {
      if (now - this.phaseAt >= HOLD_MS) this.beginMorph(now);
      else return `${BOLD}${this.word}${OFF}`;
    }
    return this.morphFrame(now);
  }

  private beginMorph(now: number): void {
    const options = MILL_WORDS.filter((w) => w !== this.word);
    this.target = options[Math.floor(this.rng() * options.length)];
    this.phaseAt = now;
    const n = Math.max(this.word.length, this.target.length);
    this.slots = [];
    let maxLand = 0;
    for (let i = 0; i < n; i++) {
      // Staggered starts sell the "one by one it comes apart" ramp; lands
      // sweep loosely left→right with jitter so letters lock in one at a
      // time rather than all at once.
      const start = this.rng() * 500;
      const land = 750 + i * 90 + this.rng() * 350;
      this.slots.push({ start, land });
      if (land > maxLand) maxLand = land;
    }
    this.morphLen = maxLand + 60;
  }

  private morphFrame(now: number): string {
    const target = this.target!;
    const t = now - this.phaseAt;
    if (t >= this.morphLen) {
      // Morph complete — the target becomes the held word.
      this.word = target;
      this.target = null;
      this.phaseAt = now;
      return `${BOLD}${this.word}${OFF}`;
    }
    let out = "";
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i];
      if (t < s.start) {
        // Still intact: shows the OLD word's char (a slot the old word
        // doesn't have simply hasn't appeared yet).
        const ch = this.word[i];
        if (ch) out += `${BOLD}${ch}${OFF}`;
      } else if (t < s.land) {
        // Flipping: lazy cadence right after start, per-frame once warm.
        const age = t - s.start;
        const period = age < LAZY_MS ? LAZY_PERIOD : FAST_PERIOD;
        out += `${DIM}${glyphAt(i, Math.floor(t / period))}${OFF}`;
      } else {
        // Landed: shows the NEW word's char (or vanishes for shrunk slots).
        const ch = target[i];
        if (ch) out += `${BOLD}${ch}${OFF}`;
      }
    }
    return out;
  }
}
